/**
 * Domain types for published swimlane views
 * These branded types ensure type safety and prevent invalid states
 */

import type { CustomFieldDTO } from '@tempo-io/item-store-dto';
import type {
  CustomerLookupEntry,
  HeaderConfig,
  HeaderDisplayValue,
  PublishedEstimation,
  PublishedEstimationScheme,
  PublishedItemLevel,
  PublishedItemStatus,
  PublishedItemType,
  PublishedTeam,
  PublishedTimelineResponse,
  TimelineLayoutDTO,
} from '@tempo-io/roadmap-dto';
import type { IHeader, TItem } from '@/components/ui/swimlane';
import type { DependencyType } from '@/lib/api/types/items';

// ============================================================================
// Password Authentication Types
// ============================================================================

/**
 * A password submission event - distinct from just a password string.
 * Each submission has identity via submittedAt, ensuring React state changes
 * even when the same password is submitted twice (retry scenario).
 */
export interface PasswordSubmission {
  readonly password: string;
  readonly submittedAt: number;
}

/**
 * Auth status for password-protected views.
 * Extracted from UsePublishedSwimlaneResult for modal props.
 */
export type PasswordAuthStatus = 'password_required' | 'password_invalid';

// ============================================================================
// Core Types
// ============================================================================

/**
 * Work item with an identifiable ID and enriched data
 * Backend returns items with numeric IDs and enriched team/status/type information
 */
export type IdentifiableTItem = TItem & {
  id: number;
  type: string; // Item type name (e.g., "Epic", "Story", "Task")
  itemTypeIcon?: string;
  itemTypeColor?: string;
  statusName?: string;
  statusColor?: string;
  teamName?: string;
  parentTitle?: string;
  parentItemTypeIcon?: string;
  parentItemTypeColor?: string;
  estimationLabel?: string;
};

/**
 * Publishing preferences for controlling what elements are displayed
 */
export interface PublishingPreferences {
  showTitle: boolean;
  showVisionStatement: boolean;
  showPublishDate: boolean;
  showLegend: boolean;
  showTempoBranding: boolean;
  hiddenFieldIds?: string[];
}

export const LegendItemIcon = {
  SOLID: 'solid',
  DASHED: 'dashed',
  TEXT_ONLY: 'text-only',
} as const;

export type LegendItemIcon = (typeof LegendItemIcon)[keyof typeof LegendItemIcon];

/**
 * Legend item representing a color-to-category mapping
 */
export interface LegendItem {
  color: string;
  label: string;
  icon?: LegendItemIcon;
  borderColor?: string;
  isHidden?: boolean;
}

/**
 * Metadata for published swimlane views
 */
export interface PublishedMetadata {
  title: string;
  visionStatement?: string;
  viewId: number;
  createdAt: string;
  publishedAt: string;
  preferences: PublishingPreferences;
}

/**
 * Layout configuration from backend
 */
export interface SwimlaneLayout {
  viewType: 'swimlane';
  itemLevelId: number;
  viewId: number;
  header1: string;
  header2: string;
  headerDirection: 'horizontal' | 'vertical';
  theme: 'standard' | 'compact';
  showEmptyHeaders: boolean;
  showItemDates: boolean;
  showGridLines?: boolean;
  showDescription?: boolean;
  showParent?: boolean;
  titleSize?: 'small' | 'medium' | 'large' | 'extra-large';
  titleHeight?: 'show-less' | 'default' | 'show-more';
  labelByHeader1?: string | null;
  labelByHeader2?: string | null;
}

/**
 * Item dependency for published swimlanes
 */
export interface ItemDependency {
  dependencyType: DependencyType;
  dependencyItemId: number;
}

/**
 * Ancestor reference for breadcrumb navigation in published item panels
 */
export interface ItemAncestorRef {
  id: number;
  name: string;
  depth: number;
}

export interface ItemDetailsSchedule {
  expectedStartDateTime: string | null;
  expectedFinishDateTime: string | null;
  latestFinishDateTime: string | null;
}

/**
 * Detailed item information for panel details
 */
export interface ItemDetails {
  id: number;
  name: string;
  description: string | null;
  itemTypeId: number;
  itemStatusId: number | undefined;
  parentId: number | null;
  teamId: number | null;
  targetStartDateTime: string | null;
  targetFinishDateTime: string | null;
  plannedStartDateTime?: string | null;
  plannedFinishDateTime?: string | null;
  customFields: Record<string, unknown>;
  timeSpentSeconds: number | null;
  estimationId: number | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  doneDateTime: string | null;
  externalKey: string | null;
  externalLink: string | null;
  planId: number | null;
  dependencies: ItemDependency[];
  ancestors?: readonly ItemAncestorRef[];
  schedule?: ItemDetailsSchedule | null;
  progress?: number | null;
}

/**
 * Backend response shape for published swimlane data
 * Backend returns pre-transformed data ready for RMSwimlane consumption
 */
export interface PublishedSwimlaneResponse {
  // RMSwimlane-ready data (backend pre-transforms to match TItem interface exactly)
  items: Record<string, IdentifiableTItem>;
  columnHeaders: IHeader[];
  rowHeaders: IHeader[];
  cells: Record<string, Record<string, string[]>>;
  // Metadata
  metadata: PublishedMetadata;
  // Legend items for color-to-category mappings
  legendItems: LegendItem[];
  // Layout configuration
  layout: SwimlaneLayout;
  // Axis labels for column and row headers
  columnAxisLabel: string;
  rowAxisLabel: string;
  // Static lookup data embedded in published context (no proxy needed)
  itemTypes: PublishedItemType[];
  itemLevels: PublishedItemLevel[];
  itemStatuses: PublishedItemStatus[];
  teams: PublishedTeam[];
  estimations: PublishedEstimation[];
  estimationSchemes: PublishedEstimationScheme[];
  // Item details for panel
  itemDetails: Record<string, ItemDetails>;
  // Custom field definitions for rendering custom field values
  customFieldDefinitions: CustomFieldDTO[];
  // Hidden field IDs configuration
  hiddenFieldIds?: readonly string[];
  // Hidden headers (filtered out before rendering)
  columnHiddenHeaderIds?: string[];
  rowHiddenHeaderIds?: string[];
  /**
   * Server-resolved MULTI_CUSTOMER lookup keyed by stringified customer id.
   * Populated when the backend has FIM data for any MULTI_CUSTOMER field on
   * this view; used by PublishedCustomFieldValue / Timeline to render chips
   * without re-hydrating FIM client-side in unauthenticated published mode.
   */
  customerLookup?: Record<string, CustomerLookupEntry>;
  // Resolved lookup maps shared across all items (stored once, not per-item)
  resolvedUsers?: Record<string, { name: string; pictureUrl: string | null }>;
  resolvedItems?: Record<string, { id: number; name: string; itemTypeId: number }>;
  resolvedCustomers?: Record<string, { id: number; name: string }>;
}

/**
 * Discriminated union for type-safe state handling in the data fetching hook
 */
export type UsePublishedSwimlaneResult =
  | { status: 'loading' }
  | { status: 'error'; error: Error }
  | { status: 'success'; data: PublishedSwimlaneResponse }
  | { status: 'password_required' }
  | { status: 'password_invalid' }
  | { status: 'not_found' };

/**
 * Cell structure for internal use: Map<rowId, Map<columnId, itemIds[]>>
 * Note: itemIds are strings, even though the item.id property is a number
 */
export type CellsMap = Map<string, Map<string, string[]>>;

export type {
  CustomerLookupEntry,
  HeaderConfig,
  HeaderDisplayValue,
  PublishedEstimation,
  PublishedEstimationScheme,
  PublishedItemLevel,
  PublishedItemStatus,
  PublishedItemType,
  PublishedTeam,
  PublishedTimelineResponse,
  TimelineLayoutDTO,
};

export type UsePublishedTimelineResult =
  | { status: 'loading' }
  | { status: 'error'; error: Error }
  | { status: 'success'; data: PublishedTimelineResponse }
  | { status: 'password_required' }
  | { status: 'password_invalid' }
  | { status: 'not_found' };

// ============================================================================
// Fetch Result Types (Type-Honest Error Handling)
// ============================================================================

/**
 * Branded type for valid HTTP error status codes (4xx-5xx).
 * Prevents representing impossible values like HTTP 0.
 *
 * The brand ensures we can't accidentally pass an arbitrary number where
 * a validated HTTP error status is expected.
 */
export type HttpErrorStatus = number & { readonly __brand: 'HttpErrorStatus' };

/**
 * Type guard to validate and brand an HTTP error status code.
 * Only 4xx and 5xx are error statuses per HTTP spec.
 *
 * @returns Branded status if valid, null otherwise
 */
export function asHttpErrorStatus(n: number): HttpErrorStatus | null {
  return n >= 400 && n <= 599 ? (n as HttpErrorStatus) : null;
}

/**
 * Discriminated union for fetch outcomes.
 *
 * This type makes illegal states unrepresentable:
 * - success: data exists, no error
 * - http_error: HTTP status exists (and is valid), error describes the failure
 * - failure: no HTTP involved (network error, validation error, etc.)
 *
 * Key insight: when there's a validation error or network failure,
 * there IS no HTTP status. We don't pretend there's a "status 0".
 */
export type FetchResult<T> =
  | { readonly type: 'success'; readonly data: T }
  | { readonly type: 'http_error'; readonly status: HttpErrorStatus; readonly error: Error }
  | { readonly type: 'failure'; readonly error: Error };
